import React from 'react';
import { Helmet } from 'react-helmet';
import { WHATSAPP_NUMBER } from '../config/subproductos';

const siteUrl = 'https://kiwidinamico.com';

// Valores por defecto de los metadatos del sitio.
const defaultTitle = 'Kiwi Dinámico | Tecnología para pymes de Costa Rica';
const defaultDescription = 'Empresa costarricense de tecnología: Kiwi ERP con factura electrónica 4.4, desarrollo de software a medida, integración con SAP Business One y licencias Microsoft para empresas.';
const defaultKeywords = 'SAP Business One, SAP HANA, desarrollo web, servicios cloud, soluciones empresariales';
// Imagen por defecto al compartir (1200x630, public/images/og-image.jpg). Ruta fija
// sin hash: es la misma que escribe scripts/html-por-ruta.js en el HTML de cada
// página, para que lo que ve WhatsApp/Facebook (sin JS) y lo que pone Helmet coincidan.
export const defaultImage = `${siteUrl}/images/og-image.jpg`;

// Datos estructurados de la empresa. Viven ESTÁTICOS en public/index.html (y de
// ahí en el HTML de cada página): Google y los validadores los leen sin ejecutar
// JS. Este objeto es la fuente con la que se compara ese bloque en
// SEO.test.js; si cambia algo acá, hay que copiarlo allá (la prueba avisa). Se arma como objeto y se serializa con
// JSON.stringify (antes era un template literal a mano, que se rompía en cuanto
// una descripción traía comillas y no había forma de validarlo).
//
// El tipo es LocalBusiness y no Organization porque "currenciesAccepted" solo
// existe en LocalBusiness dentro de schema.org; LocalBusiness es subtipo de
// Organization, así que todo consumidor que espere Organization sigue calzando.
//
// Kiwi Dinámico es una empresa costarricense: antes decía "Chile", lo que hacía
// que Google la ubicara en el país equivocado.
export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Kiwi Dinámico',
  url: siteUrl,
  // Logo cuadrado (600x600) y foto para compartir, ambos en public/images/.
  logo: `${siteUrl}/images/logo.png`,
  image: `${siteUrl}/images/og-image.jpg`,
  description: defaultDescription,
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'CR',
    addressRegion: 'San José',
    addressLocality: 'San José',
  },
  areaServed: {
    '@type': 'Country',
    name: 'Costa Rica',
  },
  currenciesAccepted: 'CRC',
  knowsLanguage: 'es-CR',
  contactPoint: {
    '@type': 'ContactPoint',
    // El número que el sitio ya publica (WHATSAPP_NUMBER), en formato E.164.
    telephone: `+${WHATSAPP_NUMBER}`,
    email: 'info@kiwidinamico.com',
    contactType: 'customer service',
    areaServed: 'CR',
    availableLanguage: 'es',
  },
};

// Se escapa "<" para que ningún valor pueda cerrar el <script> del JSON-LD.
export const organizationJsonLdString = JSON.stringify(organizationJsonLd).replace(/</g, '\\u003c');

const SEO = ({ title, description, keywords, canonical, image }) => {
  // Valores a usar (proporcionados o por defecto)
  const seoTitle = title || defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoKeywords = keywords || defaultKeywords;
  const seoImage = image || defaultImage;
  // Sin `canonical` no se emite la etiqueta (p. ej. la 404): mejor ninguna que una
  // que diga que la página es el inicio.
  const seoCanonical = canonical ? `${siteUrl}${canonical}` : null;

  return (
    <Helmet>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      {seoCanonical && <link rel="canonical" href={seoCanonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Kiwi Dinámico" />
      <meta property="og:locale" content="es_CR" />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      {seoCanonical && <meta property="og:url" content={seoCanonical} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />

      {/* El JSON-LD de la empresa NO va acá: está estático en public/index.html. */}

      {/*
        Google Analytics 4 vive SOLO en public/index.html.
        Antes también se montaba acá vía Helmet, así que cada página cargaba
        gtag.js dos veces y disparaba dos gtag('config', ...) -> page_views
        duplicados. No volver a agregar el tag en componentes React.
      */}
    </Helmet>
  );
};

export default SEO;
